import { RootState } from '../../../../store/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GetRolesRequest,
  RoleListResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  Role,
} from '../../application/dto/Role.dto';

const API_HOST = process.env.REACT_APP_API_HOST;

/**
 * Role API (RTK Query)
 * Infrastructure layer - handles HTTP communication
 */
export const roleApi = createApi({
  reducerPath: 'roleApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_HOST,
    credentials: 'include',
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info?.api_token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Role'],
  endpoints: (build) => ({
    getRoles: build.query<RoleListResponse, GetRolesRequest>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `role/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Role'],
    }),

    getRoleById: build.query<Role, string>({
      query: (id) => ({
        url: `management/role/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),

    createRole: build.mutation<Role, CreateRoleRequest>({
      query: (data) => ({
        url: 'management/role',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Role'],
    }),

    updateRole: build.mutation<Role, { id: string; data: UpdateRoleRequest }>({
      query: ({ id, data }) => ({
        url: `management/role/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Role', id }, 'Role'],
    }),

    deleteRole: build.mutation<void, string>({
      query: (id) => ({
        url: `role/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = roleApi;

